import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, User, Package, CreditCard, Truck, MapPin, Calendar, FileImage } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

const ORDER_STATUSES = ['pending', 'paid', 'processing', 'shipped', 'completed'];

export default function AdminOrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingNumber, setTrackingNumber] = useState('');

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/orders/${orderId}`);
      setData(response.data);
      setTrackingNumber(response.data.order.tracking_number || '');
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await axios.put(`${API_URL}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchOrderDetails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  };

  const handleTrackingUpdate = async () => {
    try {
      await axios.put(`${API_URL}/admin/orders/${orderId}/tracking?tracking_number=${trackingNumber}`);
      toast.success('Tracking number updated');
      fetchOrderDetails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update tracking'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper">
        <AdminSidebar />
        <div className="ml-64 p-8 flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-paper">
        <AdminSidebar />
        <div className="ml-64 p-8">
          <p className="text-ink/60">Order not found</p>
        </div>
      </div>
    );
  }

  const { order, customer, transaction } = data;
  const hasPrintItems = order.items?.some(item => item.order_type === 'print');

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Button variant="ghost" onClick={() => navigate('/admin/orders')} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>

          <div className="flex flex-wrap gap-4 justify-between items-start mb-8">
            <div className="min-w-0">
              <h1 className="font-serif text-3xl font-bold text-ink mb-2">
                {order.order_numbers?.length ? `Order ${order.order_numbers.join(', ')}` : 'Order Details'}
              </h1>
              <p className="text-ink/60 font-mono break-all">#{order.id}</p>
            </div>
            <div className="sm:text-right">
              <p className="text-3xl font-bold text-gold">${order.total_amount?.toFixed(2)}</p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                order.status === 'paid' ? 'bg-green-100 text-green-700' :
                order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                order.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{customer?.name}</p>
                <p className="text-ink/60">{customer?.email}</p>
                {customer?.phone && <p className="text-ink/60">{customer.phone}</p>}
              </CardContent>
            </Card>

            {/* Order Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Order Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-ink/60">Created: {new Date(order.created_at).toLocaleString()}</p>
                <p className="text-ink/60">Items: {order.items?.length || 0}</p>
              </CardContent>
            </Card>

            {/* Payment Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className={`font-medium ${transaction?.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {transaction?.payment_status === 'paid' ? 'Paid' : 'Pending'}
                </p>
                {transaction && (
                  <p className="text-ink/60 text-sm font-mono">Session: {transaction.session_id?.slice(0, 20)}...</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Items */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Order Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex gap-4 p-4 bg-cream rounded-lg">
                    {item.template && (
                      <img
                        src={item.template.thumbnail_image || item.template.background_image}
                        alt={item.invitation?.name}
                        className="w-20 h-28 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-serif font-semibold">{item.invitation?.name || 'Invitation'}</h4>
                      <p className="text-ink/60 text-sm">{item.template?.name}</p>
                      <div className="mt-2">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          item.order_type === 'digital' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {item.order_type === 'digital' ? 'Digital' : `Print (${item.print_quantity} cards)`}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gold">${item.price?.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Shipping Address */}
          {order.shipping_address && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{order.shipping_address.name}</p>
                <p className="text-ink/60">{order.shipping_address.street}</p>
                <p className="text-ink/60">
                  {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                </p>
                <p className="text-ink/60">{order.shipping_address.country}</p>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Order Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Update Status</Label>
                <Select value={order.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="mt-2 w-48" data-testid="order-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUSES.map((status) => (
                      <SelectItem key={status} value={status} className="capitalize">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasPrintItems && (
                <div>
                  <Label>Tracking Number</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Enter tracking number"
                      className="max-w-xs"
                      data-testid="tracking-input"
                    />
                    <Button onClick={handleTrackingUpdate} data-testid="update-tracking-btn">
                      <Truck className="h-4 w-4 mr-2" />
                      Update
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
